// components/EmployeeManagement.js
import React, { useState } from "react";
import AdminNavigation from "../../layouts/adminNavigation";
import EmployeeTable from "./components/employeeTable";
import EmployeeFilters from "./components/employeeFilters";
import EmployeeActionBar from "./components/employeeActionBar";
import EmployeeModal from "./components/employeeModal";
import EmployeeMobileCards from "./components/employeeMobileCards";
import EmployeePagination from "./components/employeePagination";
import Icon from "../../components/appIcon";
import { useAuth } from "components/ui/navigationGuard";
import Notification from "components/ui/notification";
import useEmployeeManagementLogic from "hooks/useEmployee";

const EmployeeManagement = () => {
  const { user, isAuthenticated } = useAuth();
  
  // Use the custom hook to get all the state and functions
  const {
    isNavCollapsed,
    employees,
    departments,
    selectedEmployees,
    searchTerm,
    filtersCollapsed,
    departmentFilter,
    employmentTypeFilter,
    statusFilter,
    startDate,
    endDate,
    sortConfig,
    currentPage,
    itemsPerPage,
    notification,
    modalState,
    isEditing,
    loading,
    isUnknownFilter,
    filteredAndSortedEmployees,
    paginatedEmployees,
    totalPages,
    setIsNavCollapsed,
    setSearchTerm,
    setFiltersCollapsed,
    setDepartmentFilter,
    setEmploymentTypeFilter,
    setStatusFilter,
    setStartDate,
    setEndDate,
    setSortConfig,
    setCurrentPage,
    setItemsPerPage,
    setModalState,
    setIsEditing,
    setIsUnknownFilter,
    closeNotification,
    showNotification,
    loadEmployees,
    loadDepartments,
    handleSort,
    handleSelectEmployee,
    handleSelectAll,
    handleAddEmployee,
    handleEditEmployee,
    handleViewEmployee,
    handleUploadPhoto,
    handleToggleStatus,
    handleBulkAction,
    handleSaveEmployee,
    handleClearFilters,
    handleExport,
    handlePageChange,
    handleItemsPerPageChange
  } = useEmployeeManagementLogic();

  if (loading) {
    return (
      <>
        <AdminNavigation
          isCollapsed={isNavCollapsed}
          onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
        />
        <div
          className={`min-h-screen bg-background transition-all duration-200 ${isNavCollapsed ? "lg:ml-16" : "lg:ml-64"
            } pt-16`}
        >
          <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-muted-foreground">Loading employees...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavigation
        user={user}
        isCollapsed={isNavCollapsed}
        onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
      />
      <div
        className={`min-h-screen bg-background transition-all duration-200 ${isNavCollapsed ? "lg:ml-16" : "lg:ml-64"
          } pt-16`}
      >
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                Employee Management
              </h1>
              <p className="text-muted-foreground">
                Manage known employee records, status, and information
              </p>
            </div>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Users" size={16} />
              <span>{filteredAndSortedEmployees?.length} employees</span>
            </div>
          </div>

          {/* Action Bar */}
          <EmployeeActionBar
            selectedEmployees={selectedEmployees}
            onAddEmployee={handleAddEmployee}
            onBulkAction={handleBulkAction}
            totalEmployees={employees?.length}
            filteredEmployees={filteredAndSortedEmployees?.length}
          />

          {/* Filters */}
          <EmployeeFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            isCollapsed={filtersCollapsed}
            departmentFilter={departmentFilter}
            ontoggle={() => setFiltersCollapsed(!filtersCollapsed)}
            onDepartmentChange={setDepartmentFilter}
            employmentTypeFilter={employmentTypeFilter}
            onEmploymentTypeChange={setEmploymentTypeFilter}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            startDate={startDate}
            onStartDateChange={setStartDate}
            endDate={endDate}
            onEndDateChange={setEndDate}
            onClearFilters={handleClearFilters}
            onExport={handleExport}
            isUnknownFilter={isUnknownFilter}
            onIsUnknownFilterChange={setIsUnknownFilter}
            departments={departments}
          />

          {/* Employee Table */}
          <div className="hidden md:block">
            <EmployeeTable
              employees={paginatedEmployees}
              selectedEmployees={selectedEmployees}
              onSelectEmployee={handleSelectEmployee}
              onSelectAll={handleSelectAll}
              onEditEmployee={handleEditEmployee}
              onViewEmployee={handleViewEmployee}
              onUploadPhoto={handleUploadPhoto}
              onToggleStatus={handleToggleStatus}
              sortConfig={sortConfig}
              onSort={handleSort}
              departments={departments}
            />
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            <EmployeeMobileCards
              employees={paginatedEmployees}
              selectedEmployees={selectedEmployees}
              onSelectEmployee={handleSelectEmployee}
              onEditEmployee={handleEditEmployee}
              onViewEmployee={handleViewEmployee}
              onUploadPhoto={handleUploadPhoto}
              onToggleStatus={handleToggleStatus}
              departments={departments}
            />
          </div>

          {/* Pagination */}
          <EmployeePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredAndSortedEmployees?.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </div>

      {/* Employee Modal */}
      <EmployeeModal
        employee={modalState?.employee}
        isOpen={modalState?.isOpen}
        onClose={() => setModalState({ isOpen: false, employee: null, mode: "view" })}
        onSave={handleSaveEmployee}
        mode={modalState?.mode}
        setModalState={setModalState}
        showNotification={showNotification}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        departments={departments}
      />

      {/* Notification Component */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}
    </>
  );
};

export default EmployeeManagement;